/**
 * Roving Tabindex 훅 (v3.0 - 최종 완성판)
 *
 * 모든 키보드 네비게이션 로직을 캡슐화하고,
 * 필요한 props를 getter 함수로 반환하여
 * 보일러플레이트 코드를 완전히 제거합니다.
 *
 * @features
 * - ✅ Controlled/Uncontrolled 모드 지원
 * - ✅ 방향키, Home/End, Enter/Space, Tab, Escape 모두 선택적
 * - ✅ 자동/수동 포커스 관리
 * - ✅ 자동/수동 ref 관리
 * - ✅ 다양한 props getter (기본, 탭, 옵션 등)
 * - ✅ disabled 아이템 자동 스킵
 * - ✅ SOLID 원칙 준수
 *
 * @example
 * ```tsx
 * // 독립 컴포넌트 (RadioButton, Checkbox)
 * const { getRovingProps } = useRovingTabindex({
 *   count: options.length,
 *   onSelect: (index) => handleToggle(index),
 *   direction: 'vertical',
 *   enableTabExit: true, // Tab으로 빠져나가기
 * });
 * <Checkbox {...getRovingProps(index)} />
 *
 * // 모달 내부 (SelectionModal, LanguageSelectionModal)
 * const { getRovingProps } = useRovingTabindex({
 *   count: items.length,
 *   onSelect: (index) => handleToggle(index),
 *   enableTabExit: false, // ⚠️ 필수: FocusTrap과 충돌 방지
 * });
 * <CheckboxList {...getRovingProps(index)} />
 *
 * // 드롭다운 (FilterDropdown, SortDropdown)
 * const { getOptionProps } = useRovingTabindex({
 *   count: options.length,
 *   onSelect: (index) => handleSelect(index),
 *   enableTabExit: true,
 *   enableEscape: true, // Escape로 닫기
 *   onEscape: () => setIsOpen(false),
 * });
 * <div {...getOptionProps(index)} />
 * ```
 *
 * @see docs/KEYBOARD_NAVIGATION.md
 */

import { useCallback, useEffect, useMemo, useRef, useState, MutableRefObject } from 'react';

export type RovingDirection = 'horizontal' | 'vertical' | 'both' | 'grid';

export interface UseRovingTabindexParams {
	// ========== 필수 ==========
	/** 아이템 개수 */
	count: number;

	// ========== 키보드 동작 (선택적) ==========
	/**
	 * 방향키 방향 (기본값: 'both')
	 * - `horizontal`: 좌우 방향키만
	 * - `vertical`: 위아래 방향키만
	 * - `both`: 모든 방향키 (1D 리스트)
	 * - `grid`: 2D 그리드 네비게이션 (columnsPerRow 필수)
	 */
	direction?: RovingDirection;
	/**
	 * 2D 그리드 레이아웃: 한 행당 열 개수
	 * - direction='grid'일 때 필수
	 * - 예: columnsPerRow={5} → 5개씩 한 줄
	 */
	columnsPerRow?: number;
	/** 리스트 순환 여부 (기본값: true) */
	loop?: boolean;
	/** Home 키 활성화 (기본값: true) */
	enableHome?: boolean;
	/** End 키 활성화 (기본값: true) */
	enableEnd?: boolean;
	/** Enter/Space 키 활성화 (기본값: true) */
	enableSelect?: boolean;
	/**
	 * Tab 키로 그룹 빠져나가기
	 * - `true`: 독립 컴포넌트용 (RadioButton, Pagination 등)
	 * - `false` (기본값): 모달 내부용 (useFocusTrap과 함께 사용 시)
	 *
	 * ⚠️ 주의: 모달에서 `useFocusTrap`과 함께 사용할 때는 반드시 `false`로 설정하세요.
	 * 그렇지 않으면 Tab 키 눌렀을 때 포커스가 사라지는 버그가 발생합니다.
	 *
	 * @example
	 * // ✅ 모달 내부
	 * useRovingTabindex({ count, enableTabExit: false })
	 *
	 * // ✅ 독립 컴포넌트
	 * useRovingTabindex({ count, enableTabExit: true })
	 */
	enableTabExit?: boolean;
	/** Escape 키 활성화 (기본값: false) */
	enableEscape?: boolean;

	// ========== 콜백 (선택적) ==========
	/** Enter/Space 눌렀을 때 호출 */
	onSelect?: (index: number) => void;
	/** Escape 눌렀을 때 호출 */
	onEscape?: () => void;
	/** Tab 눌렀을 때 호출 (enableTabExit이 true일 때만) */
	onTab?: (event: React.KeyboardEvent) => void;
	/** 커스텀 키 핸들러 (다른 키 추가 시) */
	onCustomKey?: (event: React.KeyboardEvent, index: number) => void;

	// ========== 상태 제어 (선택적) ==========
	/** Controlled 모드: 외부에서 focusedIndex 제어 */
	focusedIndex?: number;
	/** Controlled 모드: focusedIndex 변경 콜백 */
	onFocusedIndexChange?: (index: number) => void;
	/** Uncontrolled 모드: 초기 focusedIndex (기본값: 0) */
	defaultFocusedIndex?: number;

	// ========== Disabled 처리 (선택적) ==========
	/** 각 아이템의 disabled 상태 (배열 또는 전체 disabled) */
	disabled?: boolean[] | boolean;

	// ========== 포커스 관리 (선택적) ==========
	/** 인덱스 변경 시 자동으로 focus() 호출 여부 (기본값: true) */
	autoFocus?: boolean;
	/** 외부 ref 사용 (기본값: 내부 ref 자동 생성) */
	externalRefs?: MutableRefObject<(HTMLElement | null)[]>;

	// ========== 기타 (선택적) ==========
	/** 전체 비활성화 */
	isDisabled?: boolean;
}

export interface UseRovingTabindexReturn {
	/** 기본 Roving Tabindex props 반환 (ref, tabIndex, onKeyDown, onFocus) */
	getRovingProps: (index: number) => {
		ref: (el: HTMLElement | null) => void;
		tabIndex: number;
		onKeyDown: (e: React.KeyboardEvent) => void;
		onFocus: () => void;
	};

	/** 탭용 props 반환 (role="tab", aria-selected 포함) */
	getTabProps: (index: number, isSelected?: boolean) => {
		ref: (el: HTMLElement | null) => void;
		role: 'tab';
		tabIndex: number;
		'aria-selected': boolean;
		onKeyDown: (e: React.KeyboardEvent) => void;
		onFocus: () => void;
	};

	/** 드롭다운 옵션용 props 반환 (role="option", aria-selected 포함) */
	getOptionProps: (index: number, isSelected?: boolean) => {
		ref: (el: HTMLElement | null) => void;
		role: 'option';
		tabIndex: number;
		'aria-selected': boolean;
		onKeyDown: (e: React.KeyboardEvent) => void;
		onFocus: () => void;
	};

	/** 현재 포커스된 인덱스 (읽기 전용 또는 controlled) */
	focusedIndex: number;

	/** 포커스 인덱스 변경 함수 (필요시 외부에서 직접 제어) */
	setFocusedIndex: (index: number) => void;

	/** 특정 인덱스로 포커스 이동 (자동으로 focus() 호출) */
	focusItem: (index: number) => void;
}

/**
 * Roving Tabindex 패턴 훅
 */
export function useRovingTabindex(params: UseRovingTabindexParams): UseRovingTabindexReturn {
	const {
		count,
		direction = 'both',
		columnsPerRow,
		loop = true,
		enableHome = true,
		enableEnd = true,
		enableSelect = true,
		enableTabExit = false,
		enableEscape = false,
		onSelect,
		onEscape,
		onTab,
		onCustomKey,
		focusedIndex: controlledFocusedIndex,
		onFocusedIndexChange,
		defaultFocusedIndex = 0,
		disabled,
		autoFocus = false, // 기본값을 false로 변경 (Secure by default)
		externalRefs,
		isDisabled = false,
	} = params;

	// ========== Controlled vs Uncontrolled ==========
	const isControlled = controlledFocusedIndex !== undefined;
	const [internalFocusedIndex, setInternalFocusedIndex] = useState(defaultFocusedIndex);
	const focusedIndex = isControlled ? controlledFocusedIndex : internalFocusedIndex;

	// ========== Ref 관리 (자동 vs 수동) ==========
	const internalRefs = useRef<(HTMLElement | null)[]>([]);
	const refs = externalRefs || internalRefs;

	// ========== Disabled 배열 정규화 ==========
	const disabledArray = useMemo(() => {
		if (typeof disabled === 'boolean') {
			return Array(count).fill(disabled);
		}
		return disabled || Array(count).fill(false);
	}, [disabled, count]);

	// ========== focusedIndex 변경 함수 ==========
	const setFocusedIndex = useCallback(
		(newIndex: number) => {
			if (isControlled) {
				onFocusedIndexChange?.(newIndex);
			} else {
				setInternalFocusedIndex(newIndex);
			}
		},
		[isControlled, onFocusedIndexChange]
	);

	// ========== 자동 포커스 이동 ==========
	useEffect(() => {
		if (autoFocus && focusedIndex >= 0 && focusedIndex < count) {
			// 다음 틱에 포커스 (렌더링 완료 후)
			const timer = setTimeout(() => {
				refs.current[focusedIndex]?.focus();
			}, 0);
			return () => clearTimeout(timer);
		}
	}, [focusedIndex, autoFocus, count, refs]);

	// ========== 다음 활성화된 인덱스 찾기 (1D) ==========
	const findNextEnabledIndex = useCallback(
		(startIndex: number, step: 1 | -1): number => {
			if (count === 0) return -1;

			let nextIndex = startIndex;
			let attempts = 0;
			const maxAttempts = count;

			do {
				if (loop) {
					nextIndex = (nextIndex + step + count) % count;
				} else {
					nextIndex = nextIndex + step;
					if (nextIndex < 0 || nextIndex >= count) return startIndex;
				}

				attempts++;
				if (attempts > maxAttempts) return startIndex; // 무한 루프 방지
			} while (disabledArray[nextIndex]);

			return nextIndex;
		},
		[count, loop, disabledArray]
	);

	// ========== 2D 그리드 네비게이션 ==========
	const findNextGridIndex = useCallback(
		(currentIndex: number, key: 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown'): number => {
			if (count === 0 || !columnsPerRow) return currentIndex;

			const currentRow = Math.floor(currentIndex / columnsPerRow);
			const currentCol = currentIndex % columnsPerRow;
			const totalRows = Math.ceil(count / columnsPerRow);

			let nextRow = currentRow;
			let nextCol = currentCol;

			switch (key) {
				case 'ArrowLeft':
					nextCol--;
					if (nextCol < 0) {
						if (loop) {
							nextCol = columnsPerRow - 1;
							// 현재 행에서 실제로 존재하는 마지막 열 찾기
							const rowStartIndex = currentRow * columnsPerRow;
							const rowEndIndex = Math.min(rowStartIndex + columnsPerRow, count);
							nextCol = (rowEndIndex - rowStartIndex - 1);
						} else {
							return currentIndex; // 경계 초과
						}
					}
					break;

				case 'ArrowRight':
					nextCol++;
					const rowEndIndex = Math.min((currentRow + 1) * columnsPerRow, count);
					const maxColInRow = rowEndIndex - currentRow * columnsPerRow - 1;
					if (nextCol > maxColInRow) {
						if (loop) {
							nextCol = 0;
						} else {
							return currentIndex; // 경계 초과
						}
					}
					break;

				case 'ArrowUp':
					nextRow--;
					if (nextRow < 0) {
						if (loop) {
							nextRow = totalRows - 1;
							// 마지막 행에서 같은 열이 존재하는지 확인
							const lastRowStartIndex = nextRow * columnsPerRow;
							const lastRowSize = count - lastRowStartIndex;
							if (currentCol >= lastRowSize) {
								nextCol = lastRowSize - 1; // 마지막 행의 마지막 열로
							}
						} else {
							return currentIndex; // 경계 초과
						}
					}
					break;

				case 'ArrowDown':
					nextRow++;
					if (nextRow >= totalRows) {
						if (loop) {
							nextRow = 0;
						} else {
							return currentIndex; // 경계 초과
						}
					}
					// 다음 행에 해당 열이 없으면 마지막 열로
					const nextRowStartIndex = nextRow * columnsPerRow;
					const nextRowSize = Math.min(count - nextRowStartIndex, columnsPerRow);
					if (nextCol >= nextRowSize) {
						nextCol = nextRowSize - 1;
					}
					break;
			}

			let nextIndex = nextRow * columnsPerRow + nextCol;

			// 범위 체크
			if (nextIndex < 0 || nextIndex >= count) return currentIndex;

			// disabled 체크: 다음 활성화된 아이템 찾기
			if (disabledArray[nextIndex]) {
				// 같은 방향으로 계속 검색
				const step = key === 'ArrowRight' || key === 'ArrowDown' ? 1 : -1;
				return findNextEnabledIndex(nextIndex, step);
			}

			return nextIndex;
		},
		[count, columnsPerRow, loop, disabledArray, findNextEnabledIndex]
	);

	// ========== 키보드 핸들러 ==========
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent, index: number) => {
			if (isDisabled) return;

			// 커스텀 키 핸들러 먼저 호출
			if (onCustomKey) {
				onCustomKey(event, index);
			}

			let nextIndex = -1;
			let shouldPreventDefault = false;

			switch (event.key) {
				// ========== Escape ==========
				case 'Escape':
					if (enableEscape && onEscape) {
						event.preventDefault();
						onEscape();
					}
					return;

				// ========== Tab ==========
				case 'Tab':
					if (enableTabExit || onTab) {
						if (onTab) {
							onTab(event);
						}
						// preventDefault 하지 않음 - 자연스러운 Tab 흐름
					}
					return;

				// ========== 방향키: 오른쪽/아래 ==========
				case 'ArrowRight':
				case 'ArrowDown':
					shouldPreventDefault = true;
					if (direction === 'grid') {
						// 2D 그리드 네비게이션
						nextIndex = findNextGridIndex(index, event.key as 'ArrowRight' | 'ArrowDown');
					} else if (
						direction === 'both' ||
						(direction === 'horizontal' && event.key === 'ArrowRight') ||
						(direction === 'vertical' && event.key === 'ArrowDown')
					) {
						// 1D 리스트 네비게이션
						nextIndex = findNextEnabledIndex(index, 1);
					}
					break;

				// ========== 방향키: 왼쪽/위 ==========
				case 'ArrowLeft':
				case 'ArrowUp':
					shouldPreventDefault = true;
					if (direction === 'grid') {
						// 2D 그리드 네비게이션
						nextIndex = findNextGridIndex(index, event.key as 'ArrowLeft' | 'ArrowUp');
					} else if (
						direction === 'both' ||
						(direction === 'horizontal' && event.key === 'ArrowLeft') ||
						(direction === 'vertical' && event.key === 'ArrowUp')
					) {
						// 1D 리스트 네비게이션
						nextIndex = findNextEnabledIndex(index, -1);
					}
					break;

				// ========== Home ==========
				case 'Home':
					if (enableHome) {
						shouldPreventDefault = true;
						nextIndex = findNextEnabledIndex(-1, 1); // 처음부터 검색
					}
					break;

				// ========== End ==========
				case 'End':
					if (enableEnd) {
						shouldPreventDefault = true;
						nextIndex = findNextEnabledIndex(count, -1); // 끝에서 역방향 검색
					}
					break;

				// ========== Enter/Space ==========
				case ' ':
				case 'Enter':
					if (enableSelect && onSelect) {
						shouldPreventDefault = true;
						if (!disabledArray[index]) {
							onSelect(index);
						}
					}
					break;

				default:
					return; // 다른 키는 기본 동작 허용
			}

			if (shouldPreventDefault) {
				event.preventDefault();
			}

			// 인덱스 변경 (이동만 처리)
			if (nextIndex !== -1 && nextIndex !== index) {
				setFocusedIndex(nextIndex);
			}
		},
		[
			isDisabled,
			direction,
			enableHome,
			enableEnd,
			enableSelect,
			enableEscape,
			enableTabExit,
			onSelect,
			onEscape,
			onTab,
			onCustomKey,
			findNextEnabledIndex,
			setFocusedIndex,
			disabledArray,
			count,
		]
	);

	// ========== 수동 포커스 이동 함수 ==========
	const focusItem = useCallback(
		(index: number) => {
			if (index >= 0 && index < count) {
				setFocusedIndex(index);
				if (!autoFocus) {
					// autoFocus가 false면 수동으로 포커스
					refs.current[index]?.focus();
				}
			}
		},
		[count, setFocusedIndex, autoFocus, refs]
	);

	// ========== Props Getters ==========

	/**
	 * 기본 Roving Tabindex props
	 */
	const getRovingProps = useCallback(
		(index: number) => ({
			ref: (el: HTMLElement | null) => {
				refs.current[index] = el;
			},
			tabIndex: isDisabled ? -1 : (focusedIndex === index ? 0 : -1),
			onKeyDown: (e: React.KeyboardEvent) => handleKeyDown(e, index),
			onFocus: () => setFocusedIndex(index),
		}),
		[focusedIndex, isDisabled, handleKeyDown, setFocusedIndex, refs]
	);

	/**
	 * 탭용 props (WAI-ARIA Tabs 패턴)
	 */
	const getTabProps = useCallback(
		(index: number, isSelected = false) => ({
			...getRovingProps(index),
			role: 'tab' as const,
			'aria-selected': isSelected,
		}),
		[getRovingProps]
	);

	/**
	 * 드롭다운 옵션용 props (WAI-ARIA Listbox 패턴)
	 */
	const getOptionProps = useCallback(
		(index: number, isSelected = false) => ({
			...getRovingProps(index),
			role: 'option' as const,
			'aria-selected': isSelected,
		}),
		[getRovingProps]
	);

	return {
		getRovingProps,
		getTabProps,
		getOptionProps,
		focusedIndex,
		setFocusedIndex,
		focusItem,
	};
}
